// @ts-strict-ignore
import React, { useContext } from 'react';

import {
  Controller,
} from 'react-hook-form';
import type {
  Option,
} from '@usealloy/component-library';
import {
  Banner,
  Text,
  Box,
  Card,
  MultiSelect,
  Button,
  IconName,
} from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import type { FormattedServiceConfigOption, ServiceConfigOptionsMapping } from './types';
import { CredentialOwner, ServiceConfigModalMode } from './types';
import { ServiceConfigOptionField } from './ServiceConfigOptionFieldView';
import { ServiceConfigOptionCreation } from './ServiceConfigOptionCreationView';
import { UnknowOption, AlloyOption, CustomerOption } from './helpers/fixture';
import { LabelText, ValidationTextField } from './styles';

interface ServiceConfigOptionProps {
  mode: ServiceConfigModalMode;
  serviceConfigOptionsMapping: ServiceConfigOptionsMapping;
  formattedServiceConfigOptions: Array<FormattedServiceConfigOption>;
  watch;
  errors;
  control;
  setValue;
  trigger;
  appendServiceConfigOption;
  removeServiceConfigOption;
  selectedAlloyOptions;
  selectedCustomerOptions;
  isAddingOption;
  setIsAddingOption;
  serviceName: string;
  serviceId: number;
  submitAutoSave?: () => {};
  onlySandboxEnabled?: boolean;
}
export const ServiceConfigOption = ({
  mode,
  serviceConfigOptionsMapping,
  formattedServiceConfigOptions,
  watch,
  errors,
  control,
  setValue,
  trigger,
  appendServiceConfigOption,
  removeServiceConfigOption,
  selectedAlloyOptions,
  selectedCustomerOptions,
  isAddingOption,
  setIsAddingOption,
  serviceName,
  serviceId,
  submitAutoSave,
  onlySandboxEnabled,
}: ServiceConfigOptionProps) => {
  const { agent: { isAgent } } = useContext(UserContext);
  const credentialOwnerOption = watch('credentialOwnerOption');

  const fieldSortingFn = (
    { formattedServiceConfigOption: x }, { formattedServiceConfigOption: y },
  ) => {
    const xIsRequired = serviceConfigOptionsMapping[
      x.serviceConfigOptionId
    ]?.isRequired;
    const yIsRequired = serviceConfigOptionsMapping[
      y.serviceConfigOptionId
    ]?.isRequired;
    if (xIsRequired === yIsRequired) return 0;
    if (xIsRequired) return -1;
    return 1;
  };

  const requiredNonAuthFields = formattedServiceConfigOptions
    .map((formattedServiceConfigOption, index) => ({ formattedServiceConfigOption, index }))
    .filter(({ formattedServiceConfigOption }) => {
      const serviceConfigOptionMeta = serviceConfigOptionsMapping[
        formattedServiceConfigOption.serviceConfigOptionId
      ];
      if (serviceConfigOptionMeta) {
        return serviceConfigOptionMeta.category !== 'auth';
      }
      return false;
    }).sort(fieldSortingFn);

  const requiredAuthFields = formattedServiceConfigOptions
    .map((formattedServiceConfigOption, index) => ({ formattedServiceConfigOption, index }))
    .filter(({ formattedServiceConfigOption }) => (
      serviceConfigOptionsMapping[
        formattedServiceConfigOption.serviceConfigOptionId
      ]?.category === 'auth'
    )).sort(fieldSortingFn);

  let allCredentialOwnerOptions: Array<Option> = [];
  if (credentialOwnerOption?.value === CredentialOwner.alloy) {
    allCredentialOwnerOptions = [AlloyOption, CustomerOption];
  } else if (credentialOwnerOption?.value === CredentialOwner.customer) {
    if (isAgent) {
      allCredentialOwnerOptions = [CustomerOption];
    } else {
      allCredentialOwnerOptions = [CustomerOption, AlloyOption];
    }
  } else if (isAgent) {
    allCredentialOwnerOptions = [UnknowOption, CustomerOption];
  } else {
    allCredentialOwnerOptions = [UnknowOption, AlloyOption, CustomerOption];
  }

  const handleCredentialOwnerChange = (newOptions, onChange) => {
    onChange(newOptions);
    formattedServiceConfigOptions.forEach((option, index) => {
      if (serviceConfigOptionsMapping[option.serviceConfigOptionId]?.isOwner) {
        setValue(
          `formattedServiceConfigOptions.${index}.value` as const, '',
        );
        setValue(
          `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
          false,
        );
        trigger(`formattedServiceConfigOptions.${index}.value` as const);
      }
    });
    if (submitAutoSave) {
      submitAutoSave();
    }
  };

  return (
    <Box
      direction={mode === ServiceConfigModalMode.CUSTOMER ? 'column' : 'row'}
      gap={2}
      justifyContent="flex-start"
      style={{ alignItems: 'flex-start' }}
    >
      {
        (mode === ServiceConfigModalMode.CUSTOMER) && (
          <Banner
            borderColor="var(--cl-juicyBlue-400)"
            open
            outline
            variant="info"
          >
            <Text lineHeight={4} as="span">
              This configuration will be used for this Service across all workflows unless
              overwritten at the workflow level.
            </Text>
          </Banner>
        )
      }
      {
        (mode === ServiceConfigModalMode.CUSTOMER) && (
          <Card
            collapsible
            header="Authentication"
            style={{ border: 'none', padding: '0px' }}
            showContentSeparator={false}
            compact
          >
            <Box direction="column" gap={4} justifyContent="flex-start">
              <Box direction="column" gap={1} justifyContent="flex-start" alignItems="flex-start">
                <Controller
                  control={control}
                  name="credentialOwnerOption"
                  render={({ field: { onChange, value } }) => (
                    <MultiSelect
                      label="Owner"
                      required
                      error={errors.credentialOwner?.message}
                      onChange={newOptions => {
                        handleCredentialOwnerChange(newOptions, onChange);
                      }}
                      options={allCredentialOwnerOptions}
                      selectedOptions={value}
                      isMulti={false}
                    />
                  )}
                />
                {credentialOwnerOption?.value === CredentialOwner.alloy ? (
                  <ValidationTextField>
                    You are currently using Alloy credentials for this service.
                  </ValidationTextField>
                ) : ''}
                {credentialOwnerOption?.value === CredentialOwner.customer ? (
                  <ValidationTextField>
                    You will be paying for the usage of these credentials directly.
                  </ValidationTextField>
                ) : ''}
              </Box>
              {requiredAuthFields.map(({ formattedServiceConfigOption, index }) => (
                <ServiceConfigOptionField
                  key={`service-confi-option-field-auth-${index}`}
                  formattedServiceConfigOption={formattedServiceConfigOption}
                  serviceConfigOptionMeta={
                    serviceConfigOptionsMapping[formattedServiceConfigOption.serviceConfigOptionId]
                  }
                  index={index}
                  errors={errors}
                  control={control}
                  setValue={setValue}
                  trigger={trigger}
                  removeServiceConfigOption={removeServiceConfigOption}
                  serviceId={serviceId}
                  credentialOwner={credentialOwnerOption?.value}
                  submitAutoSave={submitAutoSave}
                  onlySandboxEnabled={onlySandboxEnabled}
                />
              ))}
            </Box>
          </Card>
        )
      }

      <Box direction="column" gap={4} justifyContent="flex-start">
        <Card
          collapsible
          header={(
            <Text
              data-dd-action-name="service-config-option-configuration-header"
              size={4}
              weight={500}
            >
              Configuration
            </Text>
          )}
          style={{ border: 'none', padding: '0px' }}
          showContentSeparator={false}
          compact
        >
          <Box direction="column" gap={4} justifyContent="flex-start">
            {requiredNonAuthFields.map(({ formattedServiceConfigOption, index }) => (
              <ServiceConfigOptionField
                key={`service-confi-option-field-configuration-${index}`}
                formattedServiceConfigOption={formattedServiceConfigOption}
                serviceConfigOptionMeta={
                  serviceConfigOptionsMapping[formattedServiceConfigOption.serviceConfigOptionId]
                }
                index={index}
                errors={errors}
                control={control}
                setValue={setValue}
                trigger={trigger}
                removeServiceConfigOption={removeServiceConfigOption}
                serviceId={serviceId}
                credentialOwner={credentialOwnerOption?.value}
                submitAutoSave={submitAutoSave}
                onlySandboxEnabled={onlySandboxEnabled}
              />
            ))}
            {(!isAddingOption) && (
              <Box direction="column" justifyContent="flex-start" alignItems="flex-start">
                <Button
                  data-dd-action-name="service-config-option-add-configuration-option"
                  icon={IconName.Add}
                  onClick={() => setIsAddingOption(!isAddingOption)}
                  stretch
                  style={{ justifyContent: 'flex-start' }}
                >
                  Add Configuration Option
                </Button>
                <LabelText>
                  Explore the various options for {serviceName} and add them to this instance
                  of the service
                </LabelText>
              </Box>

            )}
            {(isAddingOption) && (
              <ServiceConfigOptionCreation
                mode={mode}
                serviceConfigOptions={Object.values(serviceConfigOptionsMapping)}
                formattedServiceConfigOptions={formattedServiceConfigOptions}
                cancelAdding={() => setIsAddingOption(!isAddingOption)}
                appendServiceConfigOption={appendServiceConfigOption}
                selectedAlloyOptions={selectedAlloyOptions}
                selectedCustomerOptions={selectedCustomerOptions}
                submitAutoSave={submitAutoSave}
              />
            )}
          </Box>
        </Card>
      </Box>

      {
        (mode === ServiceConfigModalMode.NODE) && (
          <Card
            collapsible
            header="Authentication"
            style={{ border: 'none', padding: '0px' }}
            showContentSeparator={false}
            compact
          >
            <Box direction="column" gap={4} justifyContent="flex-start">
              <Box direction="column" gap={1} justifyContent="flex-start" alignItems="flex-start">
                <Controller
                  control={control}
                  name="credentialOwnerOption"
                  render={({ field: { onChange, value } }) => (
                    <MultiSelect
                      label="Owner"
                      required={!onlySandboxEnabled}
                      isDisabled={onlySandboxEnabled}
                      error={errors.credentialOwner?.message}
                      onChange={newOptions => {
                        handleCredentialOwnerChange(newOptions, onChange);
                      }}
                      options={allCredentialOwnerOptions}
                      selectedOptions={value}
                      isMulti={false}
                    />
                  )}
                />
                {credentialOwnerOption?.value === CredentialOwner.alloy ? (
                  <ValidationTextField>
                    You are currently using Alloy credentials for this service.
                  </ValidationTextField>
                ) : ''}
                {credentialOwnerOption?.value === CredentialOwner.customer ? (
                  <ValidationTextField>
                    You will be paying for the usage of these credentials directly.
                  </ValidationTextField>
                ) : ''}
              </Box>
              {requiredAuthFields.map(({ formattedServiceConfigOption, index }) => (
                <ServiceConfigOptionField
                  key={`service-confi-option-auth-${index}`}
                  formattedServiceConfigOption={formattedServiceConfigOption}
                  serviceConfigOptionMeta={
                    serviceConfigOptionsMapping[formattedServiceConfigOption.serviceConfigOptionId]
                  }
                  index={index}
                  errors={errors}
                  control={control}
                  setValue={setValue}
                  trigger={trigger}
                  removeServiceConfigOption={removeServiceConfigOption}
                  serviceId={serviceId}
                  credentialOwner={credentialOwnerOption?.value}
                  submitAutoSave={submitAutoSave}
                  onlySandboxEnabled={onlySandboxEnabled}
                />
              ))}
            </Box>
          </Card>
        )
      }
    </Box>
  );
};
